import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Timer, Check } from 'lucide-react';
import { useTimer } from '../../context/TimerContext';
import { redirectToCheckout } from '../../lib/stripe';
import { useDisplayPrice } from '../../lib/stripe';

export function PaywallModal() {
  const displayPrice = useDisplayPrice();
  const { showUpgradeModal, setShowUpgradeModal } = useTimer();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleCheckout = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await redirectToCheckout('pomoTimer');
    } catch (error) {
      console.error('Failed to redirect to checkout:', error);
      setError(error instanceof Error ? error.message : 'Failed to process checkout');
      setIsLoading(false);
    }
  };

  return (
    <Dialog.Root open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <Timer className="w-6 h-6 text-indigo-600 dark:text-indigo-400 mr-2" />
              <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white">
                Upgrade to Premium
              </Dialog.Title>
            </div>
            <Dialog.Close className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <X className="w-5 h-5" />
            </Dialog.Close>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              ${displayPrice ?? import.meta.env.VITE_PRICE_LIFETIME}
              <span className="text-base font-normal text-gray-500 dark:text-gray-400"> one-time payment</span>
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Unlock all premium features and support our development.
            </p>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex items-start">
              <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-gray-700 dark:text-gray-300">Unlimited focus sessions and cycles</p>
            </div>
            <div className="flex items-start">
              <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-gray-700 dark:text-gray-300">Custom audio for focus, break, and completion sounds</p>
            </div>
            <div className="flex items-start">
              <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-gray-700 dark:text-gray-300">Save and load custom timer presets</p>
            </div>
            <div className="flex items-start">
              <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-gray-700 dark:text-gray-300">Priority support and future updates</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <button
            onClick={handleCheckout}
            disabled={isLoading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Upgrade Now'
            )}
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}